/**
 * @fileoverview Typography components - Atomic UI elements
 * 
 * Heading and text components (h1-h6, p, span) with consistent styling.
 * Built with Tailwind CSS and CVA for variant management.
 * 
 * @module ui/shared/atoms/Typography
 */

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

/**
 * Heading variant definitions using CVA
 * Using !important to override global Ant Design styles
 */
const headingVariants = cva(
  "font-bold text-gray-900 tracking-tight !text-gray-900",
  {
    variants: {
      level: {
        h1: "text-4xl sm:text-5xl !text-gray-900",
        h2: "text-3xl sm:text-4xl !text-gray-900",
        h3: "text-2xl sm:text-3xl !text-gray-900",
        h4: "text-xl sm:text-2xl !text-gray-900",
        h5: "text-lg sm:text-xl !text-gray-900",
        h6: "text-base sm:text-lg !text-gray-900",
      },
    },
    defaultVariants: {
      level: "h1",
    },
  }
);

/**
 * Text variant definitions using CVA
 */
const textVariants = cva("text-gray-900", {
  variants: {
    variant: {
      default: "text-base",
      small: "text-sm",
      large: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    textColor: {
      default: "text-gray-900",
      secondary: "text-gray-600",
      muted: "text-gray-500",
      disabled: "text-gray-400",
        primary: "text-primary-600",
      success: "text-green-600",
      warning: "text-yellow-600",
      danger: "text-red-600",
    },
  },
  defaultVariants: {
    variant: "default",
    weight: "normal",
    textColor: "default",
  },
});

/**
 * Heading component props
 */
interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  /**
   * Heading level (h1-h6)
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
}

/**
 * Text component props
 */
interface TextProps
  extends Omit<React.HTMLAttributes<HTMLParagraphElement>, "color">,
    VariantProps<typeof textVariants> {
  /**
   * Render as a different element
   */
  as?: "p" | "span" | "div";
  /**
   * Text color (renamed from color to avoid conflict with HTML color attribute)
   */
  color?: VariantProps<typeof textVariants>["textColor"];
}

/**
 * H1 Component
 * 
 * @param {HeadingProps} props - Heading props
 * @returns {JSX.Element} H1 element
 */
const H1 = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = "h1", as = "h1", ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level: "h1" }), className)}
        {...props}
      />
    );
  }
);
H1.displayName = "H1";

/**
 * H2 Component
 * 
 * @param {HeadingProps} props - Heading props
 * @returns {JSX.Element} H2 element
 */
const H2 = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = "h2", as = "h2", ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level: "h2" }), className)}
        {...props}
      />
    );
  }
);
H2.displayName = "H2";

/**
 * H3 Component
 * 
 * @param {HeadingProps} props - Heading props
 * @returns {JSX.Element} H3 element
 */
const H3 = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = "h3", as = "h3", ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level: "h3" }), className)}
        {...props}
      />
    );
  }
);
H3.displayName = "H3";

/**
 * H4 Component
 * 
 * @param {HeadingProps} props - Heading props
 * @returns {JSX.Element} H4 element
 */
const H4 = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = "h4", as = "h4", ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level: "h4" }), className)}
        {...props}
      />
    );
  }
);
H4.displayName = "H4";

/**
 * H5 Component
 * 
 * @param {HeadingProps} props - Heading props
 * @returns {JSX.Element} H5 element
 */
const H5 = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = "h5", as = "h5", ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level: "h5" }), className)}
        {...props}
      />
    );
  }
);
H5.displayName = "H5";

/**
 * H6 Component
 * 
 * @param {HeadingProps} props - Heading props
 * @returns {JSX.Element} H6 element
 */
const H6 = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level = "h6", as = "h6", ...props }, ref) => {
    const Component = as;
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ level: "h6" }), className)}
        {...props}
      />
    );
  }
);
H6.displayName = "H6";

/**
 * Text Component
 * 
 * Flexible text component with variants for size, weight, and color.
 * 
 * @param {TextProps} props - Text props
 * @returns {JSX.Element} Text element
 * 
 * @example
 * <Text variant="large" weight="semibold" color="primary">
 *   Hello World
 * </Text>
 */
const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  ({ className, variant, weight, color, textColor, as = "p", ...props }, ref) => {
    const Component = as;
    // Use color prop if provided, otherwise use textColor
    const finalTextColor = color || textColor;
    return (
      <Component
        ref={ref}
        className={cn(textVariants({ variant, weight, textColor: finalTextColor }), className)}
        {...props}
      />
    );
  }
);
Text.displayName = "Text";

// Named exports for all typography components
export { H1, H2, H3, H4, H5, H6, Text, headingVariants, textVariants };
export type { HeadingProps, TextProps };

